import { LIST_SCOPES, MINUTES_PER_DAY, PARITY } from './constants';
import type { OptionListKey, Parity } from './constants';
import type { RunValidationNode, RunValidations } from './types';

// The request stamp and the saga's reply both derive the scope here. A mismatch between the two is
// not a wrong value but a picker that loads for good, so the rule may exist in only one place.
export function deriveListScope(list: OptionListKey, scopeId?: string | null): string | null {
  return LIST_SCOPES[list] ? scopeId ?? null : null;
}

// The other half of the guard key, normalised on both sides for the same reason.
export function normaliseSearch(search?: string | null): string {
  return search ?? '';
}

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

export function parseTimeToMinutes(time: string | null | undefined): number | null {
  if (!time) return null;

  const match = TIME_PATTERN.exec(time);

  if (!match) return null;

  return Number(match[1]) * 60 + Number(match[2]);
}

// An end at or before the start means the shift wraps to the next day: end + 24h - start.
export function getShiftLengthMinutes(startTime: string | null, endTime: string | null): number | null {
  const start = parseTimeToMinutes(startTime);
  const end = parseTimeToMinutes(endTime);

  if (start === null || end === null) return null;

  return end > start ? end - start : end + MINUTES_PER_DAY - start;
}

export function isNextDayShift(startTime: string | null, endTime: string | null): boolean {
  const start = parseTimeToMinutes(startTime);
  const end = parseTimeToMinutes(endTime);

  if (start === null || end === null) return false;

  return end <= start;
}

// Derived from length of service, never entered, so it cannot contradict the weeks.
export function deriveParity(tenureWeeks: number, postParityMinWeeks: number): Parity {
  return tenureWeeks >= postParityMinWeeks ? PARITY.POST : PARITY.PRE;
}

// `null` covers both "nothing entered yet" and "boundary not known yet", which the parity display
// treats the same way: it shows nothing rather than guessing.
export function deriveParityFromInput(tenureWeeks: string, postParityMinWeeks: number | null): Parity | null {
  // `!= null`, not `=== null`: an `undefined` slipping through would make `weeks >= undefined`
  // false and quietly read every worker as Pre-parity. A boundary of 0 must still pass.
  if (postParityMinWeeks == null) return null;

  const trimmed = tenureWeeks?.trim();

  if (!trimmed) return null;

  const weeks = Number(trimmed);

  if (!Number.isFinite(weeks) || !Number.isInteger(weeks) || weeks < 0) return null;

  return deriveParity(weeks, postParityMinWeeks);
}

// `undefined` is the first pass on mount and `null` the first pick — neither is a change, so
// a deep-linked pick is honoured and there is nothing scoped to a previous one to drop.
export function shouldClearScoped(previous: string | null | undefined, next: string | null): boolean {
  if (previous === undefined || previous === null) return false;

  return previous !== next;
}

// A filter narrows a list something was already chosen from, so taking one up for the first time can
// orphan that choice too. Only `undefined`, the first pass on mount, changes nothing.
export function shouldClearOnFilterChange(previous: string | null | undefined, next: string | null): boolean {
  return previous !== undefined && previous !== next;
}

// A blank field is a deliberate zero — a new period for hours, no days worked for days — so the
// run always receives a number.
export function toNumberOrZero(value: string): number {
  const trimmed = value?.trim();

  if (!trimmed) return 0;

  const parsed = Number(trimmed);

  return Number.isFinite(parsed) ? parsed : 0;
}

// A key carries a list of messages, but a bare string arrives too, and reading `[0]` off that would
// render its first character as the whole error.
function messagesOf(value: RunValidationNode): string[] {
  if (Array.isArray(value)) return value.filter((entry): entry is string => typeof entry === 'string' && !!entry);

  return typeof value === 'string' && value ? [value] : [];
}

function isValidationMap(value: unknown): value is RunValidations {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;

  return Object.values(value).every(entry => {
    if (typeof entry === 'string') return true;

    if (Array.isArray(entry)) return entry.every(message => typeof message === 'string');

    return isValidationMap(entry);
  });
}

// The transport parks the wire's map on the rejection untyped, so it is taken only where it reads as the
// grammar the flattening below walks. Anything else is treated as no map at all, and the run stays generic.
export function asRunValidations(value: unknown): RunValidations | null {
  return isValidationMap(value) ? value : null;
}

// A nested entity's errors arrive under its own map, so they are read out under dotted paths:
// `{ hours_worked_to_date: { weekly: ['x'] } }` becomes `[['hours_worked_to_date.weekly', ['x']]]`.
export function flattenValidations(
  validations: RunValidations | null | undefined,
  prefix = '',
): Array<[string, string[]]> {
  if (!validations) return [];

  return Object.entries(validations).flatMap(([key, value]): Array<[string, string[]]> => {
    const path = prefix ? `${prefix}.${key}` : key;

    if (value && typeof value === 'object' && !Array.isArray(value)) return flattenValidations(value, path);

    return [[path, messagesOf(value)]];
  });
}
